import {importLibrary, setOptions} from '@googlemaps/js-api-loader'

let configured = false
let placesLibraryPromise

/**
 * The prediction types the street field asks Places for (Table A; up to five
 * are allowed): street addresses, and subpremises so a typed unit ("1350
 * Beacon St Apt 4") resolves to a place that carries it.
 */
export const ADDRESS_PRIMARY_TYPES = ['street_address', 'subpremise']

export function loadGooglePlacesLibrary() {
  const key = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY

  if (!key || typeof window === 'undefined') {
    return Promise.resolve(null)
  }

  if (!configured) {
    setOptions({
      key,
      v: 'weekly',
      language: 'en',
      region: 'US',
      authReferrerPolicy: 'origin',
    })
    configured = true
  }

  placesLibraryPromise ??= importLibrary('places')
  return placesLibraryPromise
}

export function getStructuredAddress(place) {
  const components = place?.addressComponents ?? []
  const byType = new Map()

  for (const component of components) {
    for (const type of component.types ?? []) {
      if (!byType.has(type)) byType.set(type, component)
    }
  }

  const longText = type => byType.get(type)?.longText ?? ''
  const shortText = type => byType.get(type)?.shortText ?? ''
  const streetAddress = [longText('street_number'), longText('route')]
    .filter(Boolean)
    .join(' ')
  const city =
    longText('locality') ||
    longText('postal_town') ||
    longText('sublocality_level_1') ||
    longText('administrative_area_level_2')
  const postalCode = longText('postal_code')
  const postalSuffix = longText('postal_code_suffix')

  return {
    address: streetAddress,
    secondAddress: formatSubpremise(longText('subpremise')),
    city,
    state: shortText('administrative_area_level_1'),
    zip:
      postalCode && postalSuffix ? `${postalCode}-${postalSuffix}` : postalCode,
    country: shortText('country'),
  }
}

// A unit at the end of the typed street line: a designator and its value
// ("Apt 4", "Suite 3300", "Ste. 200", "Fl 2", "Unit B", "Apt #4") or a bare
// "#12". The value holds a digit or is a single letter, so a street or a
// city ("Unit Rd", "Ste Genevieve") is never read as a unit.
const UNIT_DESIGNATOR = '(?:apartment|apt|unit|suite|ste|floor|fl)'
const UNIT_VALUE = '(?:\\d[a-z0-9-]*|[a-z]\\d[a-z0-9-]*|[a-z])'
const TYPED_UNIT = new RegExp(
  `(?:^|[\\s,])(${UNIT_DESIGNATOR}\\.?\\s*#?\\s*${UNIT_VALUE}|#\\s*[a-z0-9][a-z0-9-]*)[\\s,]*$`,
  'i',
)
const LEADING_DESIGNATOR = new RegExp(`^${UNIT_DESIGNATOR}\\.?`, 'i')

/** The unit fragment at the end of the typed street line, verbatim, or ''. */
export function getTypedUnit(text) {
  const match = String(text ?? '').match(TYPED_UNIT)
  return match ? match[1].trim() : ''
}

/**
 * Google's `subpremise` as a second address line: its designator capitalised
 * ("apt 4" → "Apt 4"); a bare value takes the USPS "#" ("4" → "#4").
 */
export function formatSubpremise(subpremise) {
  const unit = String(subpremise ?? '').trim()
  if (!unit) return ''
  if (/^(?:[a-z0-9-]*\d[a-z0-9-]*|[a-z])$/i.test(unit)) return `#${unit}`
  return unit.charAt(0).toUpperCase() + unit.slice(1)
}

// "Apt 4", "apt 4" and "#4" are the same unit.
function unitValue(unit) {
  return unit
    .replace(LEADING_DESIGNATOR, '')
    .replace(/[\s#.]/g, '')
    .toLowerCase()
}

/**
 * The second address line a selected place implies (O450): the unit the
 * applicant typed on the street line, verbatim, when the place has none or
 * the same one; otherwise the place's own unit (the applicant picked a
 * different one). '' when neither has a unit.
 */
export function getUnitLine(typedStreet, placeUnit = '') {
  const typedUnit = getTypedUnit(typedStreet)
  if (
    typedUnit &&
    (!placeUnit || unitValue(typedUnit) === unitValue(placeUnit))
  ) {
    return typedUnit
  }
  return placeUnit
}
